import { Module } from "vuex";
import SignalRService from "@/services/signalRService";

import {
  register,
  resendCode,
  confirmUser,
  getCompanyInfo
} from "@/api.resource/api/root";
import {
  login,
  forgotPassword,
  resetPassword,
  logout,
  mfaLogin,
  refreshToken,
  verifyUserLogin
} from "@/api.resource/api/auth";
import TokenService from "@/api.resource/token.service";
import { IAuthState, IRootState } from "@/types";
// import { useTransitionState } from "@vue/runtime-core";

const auth: Module<IAuthState, IRootState> = {
  namespaced: true,
  state: (): IAuthState => ({
    otpEmail: null,
    loading: false,
    otpLoading: false,
    success: false,
    otpErr: null,
    otpSuccess: false,
    registerError: false,
    registerErrorMsg: null,
    loginMsg: null,
    loginErrorStatus: null,
    resetPasswordSuccess: false,
    resetPasswordMsg: null,
    resendOtpErr: null,
    otpValidationLoading: false,
    userMail: "",
    userData: null,
    otpValidated: false,
    validationError: null,
    validationSuccess: null,
    logoutSuccess: false,
    logoutMsg: null,
    mfaLoginSuccess: false,
    mfaLoginMsg: null,
    companyInfo: null,
    companyInfoSuccess: false,
    companyInfoMsg: null,
    token: TokenService.getAccessToken(),
    otpCode: "",
    MCAdminCognitoID: "",
    rcNumber: "",
    tempRegisterInfo: null,
    loadingInfo: false,
    refreshTokenLoading: false,
    refreshTokenSuccess: false,
    refreshTokenMsg: null,
    refreshTokenData: null,
    mfaEnabled: false,
    accessToken: null,
    verifyUserLoading: false,
    verifyUserSuccess: false,
    verifyUserMsg: null,
    resendCodeLoading: false
  }),
  getters: {
    loading: state => state.loading,
    userMail: state => state.userMail,
    otpEmail: state => state.otpEmail,
    userData: state => state.userData,
    loginMsg: state => state.loginMsg,
    loginErrorStatus: state => state.loginErrorStatus,
    resetPasswordSuccess: state => state.resetPasswordSuccess,
    resetPasswordMsg: state => state.resetPasswordMsg,
    otpLoading: state => state.otpLoading,
    success: state => state.success,
    otpSuccess: state => state.otpSuccess,
    otpErr: state => state.otpErr,
    resendOtpErr: state => state.resendOtpErr,
    otpValidated: state => state.otpValidated,
    isAuthenticated: state => !!state.userData,
    validationError: state => state.validationError,
    validationSuccess: state => state.validationSuccess,
    registerError: state => state.registerError,
    registerErrorMsg: state => state.registerErrorMsg,
    otpValidationLoading: state => state.otpValidationLoading,
    logoutSuccess: state => state.logoutSuccess,
    logoutMsg: state => state.logoutMsg,
    mfaLoginSuccess: state => state.mfaLoginSuccess,
    mfaLoginMsg: state => state.mfaLoginMsg,
    companyInfo: state => state.companyInfo,
    companyInfoSuccess: state => state.companyInfoSuccess,
    companyInfoMsg: state => state.companyInfoMsg,
    token: state => state.token,
    otpCode: state => state.otpCode,
    MCAdminCognitoID: state => state.MCAdminCognitoID,
    rcNumber: state => state.rcNumber,
    tempRegisterInfo: state => state.tempRegisterInfo,
    loadingInfo: state => state.loadingInfo,
    refreshTokenLoading: state => state.refreshTokenLoading,
    refreshTokenSuccess: state => state.refreshTokenSuccess,
    refreshTokenMsg: state => state.refreshTokenMsg,
    refreshTokenData: state => state.refreshTokenData,
    mfaEnabled: state => state.mfaEnabled,
    accessToken: state => state.accessToken,
    verifyUserLoading: state => state.verifyUserLoading,
    verifyUserSuccess: state => state.verifyUserSuccess,
    verifyUserMsg: state => state.verifyUserMsg,
    resendCodeLoading: state => state.resendCodeLoading
  },
  mutations: {
    MFA_ENABLED(state, payload) {
      state.mfaEnabled = payload;
    },
    LOADING(state) {
      state.loading = true;
    },
    LOADING_STOP(state) {
      state.loading = false;
    },
    OTP_LOADING(state) {
      state.otpLoading = true;
    },
    OTP_LOADING_STOP(state) {
      state.otpLoading = false;
    },
    OTP_CODE(state, payload) {
      state.otpCode = payload;
    },
    VERIFY_USER_LOADING(state) {
      state.verifyUserLoading = true;
    },
    VERIFY_USER_LOADING_STOP(state) {
      state.verifyUserLoading = false;
    },
    RESEND_CODE_LOADING(state) {
      state.resendCodeLoading = true;
    },
    RESEND_CODE_LOADING_STOP(state) {
      state.resendCodeLoading = false;
    },
    VERIFY_USER_SUCCESS(state, payload) {
      state.verifyUserLoading = false;
      state.verifyUserSuccess = true;
      state.verifyUserMsg = payload;
    },
    VERIFY_USER_ERROR(state, payload) {
      state.verifyUserLoading = false;
      state.verifyUserSuccess = false;
      state.verifyUserMsg = payload;
    },
    RESET_VERIFY_USER(state) {
      state.verifyUserLoading = false;
      state.verifyUserSuccess = false;
      state.verifyUserMsg = null;
    },
    OTP_SUCCESS(state, payload) {
      state.otpLoading = false;
      state.otpSuccess = true;
      state.otpErr = payload;
    },
    OTP_ERROR(state, payload) {
      state.otpLoading = false;
      state.otpSuccess = false;
      state.otpErr = payload;
    },
    RESET_OTP_PROCESS(state) {
      state.otpLoading = false;
      state.otpSuccess = false;
      state.otpErr = null;
      state.resendOtpErr = null;
      state.validationError = null;
      state.validationSuccess = null;
      state.otpValidated = false;
      state.otpValidationLoading = false;
    },
    LOGOUT_SUCCESS(state, payload) {
      state.loading = false;
      state.userData = null;
      state.logoutSuccess = true;
      state.logoutMsg = payload;
      TokenService.removeToken();
      state.userMail = "";
      state.MCAdminCognitoID = "";
    },
    LOGOUT_ERROR(state, payload) {
      state.loading = false;
      state.logoutSuccess = false;
      state.logoutMsg = payload;
    },
    RESET_LOGOUT_PROCESS(state) {
      state.loading = false;
      state.logoutSuccess = false;
      state.logoutMsg = null;
    },
    REGISTER_SUCCESS(state, payload) {
      state.loading = false;
      state.registerError = false;
      state.userData = payload?.result?.data;
      state.registerErrorMsg = payload?.message;
    },
    REGISTER_ERROR(state, payload) {
      state.loading = false;
      state.registerError = true;
      state.registerErrorMsg = payload;
    },
    RESET_REGISTER_PROCESS(state) {
      state.loading = false;
      state.otpEmail = null;
      state.otpErr = null;
      state.otpLoading = false;
      state.otpSuccess = false;
      state.resendOtpErr = null;
      state.registerError = false;
      state.validationError = null;
      state.otpValidated = false;
      state.otpValidationLoading = false;
      state.registerErrorMsg = null;
    },
    USER_MAIL(state, payload) {
      state.userMail = payload;
    },
    MC_COGNITO_ID(state, payload) {
      state.MCAdminCognitoID = payload;
    },
    RC_NUMBER(state, payload) {
      state.rcNumber = payload;
    },
    LOGIN_SUCCESS(state, payload) {
      state.loading = false;
      state.success = true;
      state.userData = payload;
      state.loginMsg = payload?.Description;
    },
    LOGIN_ERROR(state, payload) {
      state.loading = false;
      state.success = false;
      state.loginMsg = payload?.result?.data?.Description;
      state.loginErrorStatus = payload?.status;
    },
    RESET_LOGIN_PROCESS(state) {
      state.loading = false;
      state.otpEmail = null;
      state.otpErr = null;
      state.otpLoading = false;
      state.otpSuccess = false;
      state.resendOtpErr = null;
      state.loginMsg = null;
      state.loginErrorStatus = null;
      state.validationError = null;
      state.otpValidated = false;
      state.otpValidationLoading = false;
      state.success = false;
    },
    RESEND_OTP_SUCCESS(state, payload) {
      state.loading = false;
      state.otpSuccess = true;
      state.resendOtpErr = payload.message;
      state.userData = payload?.result?.data;
    },
    RESEND_OTP_ERROR(state, payload) {
      state.loading = false;
      state.otpSuccess = false;
      state.resendOtpErr = payload.message;
    },
    OTP_VALIDATION_LOADING(state) {
      state.otpValidationLoading = true;
    },
    OTP_VALIDATION_LOADING_STOP(state) {
      state.otpValidationLoading = false;
    },
    VALIDATION_ERROR(state, payload) {
      state.validationError = payload;
      state.otpValidationLoading = false;
      state.otpValidated = false;
    },
    VALIDATION_SUCCESS(state, payload) {
      state.otpValidationLoading = false;
      state.otpValidated = true;
      state.validationSuccess = payload;
    },
    RESET_PASSWORD_SUCCESS(state, payload) {
      state.loading = false;
      state.resetPasswordSuccess = true;
      state.resetPasswordMsg = payload;
    },
    RESET_PASSWORD_ERROR(state, payload) {
      state.loading = false;
      state.resetPasswordSuccess = false;
      state.resetPasswordMsg = payload;
    },
    RESET_PASSWORD_RESET(state) {
      state.loading = false;
      state.resetPasswordSuccess = false;
      state.resetPasswordMsg = null;
    },
    MFA_LOGIN_SUCCESS(state, payload) {
      state.loading = false;
      state.mfaLoginSuccess = true;
      state.mfaLoginMsg = payload;
    },
    MFA_LOGIN_ERROR(state, payload) {
      state.loading = false;
      state.mfaLoginSuccess = false;
      state.mfaLoginMsg = payload;
    },
    MFA_LOGIN_RESET(state) {
      state.loading = false;
      state.mfaLoginSuccess = false;
      state.mfaLoginMsg = null;
    },
    GET_COMPANY_SUCCESS(state, payload) {
      state.loadingInfo = false;
      state.companyInfoSuccess = true;
      state.companyInfoMsg = payload?.message;
      state.companyInfo = payload?.result?.data;
    },
    GET_COMPANY_ERROR(state, payload) {
      state.loadingInfo = false;
      state.companyInfoSuccess = false;
      state.companyInfoMsg = payload;
      state.companyInfo = null;
    },
    RESET_COMPANY_INFO(state, payload) {
      state.loadingInfo = false;
      state.companyInfoSuccess = false;
      state.companyInfoMsg = null;
      state.companyInfo = null;
    },
    SAVE_TEMP_REGISTER_INFO(state, payload) {
      state.tempRegisterInfo = payload;
    },
    SET_POLICY_TERMS_VALUE(state, payload) {
      if (!state.tempRegisterInfo) return;

      if ("policyCheck" in state.tempRegisterInfo) {
        state.tempRegisterInfo.policyCheck = payload;
      }
    },
    CLEAR_TEMP_REGISTER_INFO(state) {
      state.tempRegisterInfo = null;
    },
    LOADING_INFO(state) {
      state.loadingInfo = true;
    },
    LOADING_INFO_STOP(state) {
      state.loadingInfo = false;
    },
    REFRESH_TOKEN_LOADING(state, payload: boolean) {
      state.refreshTokenLoading = payload;
    },
    REFRESH_TOKEN_SUCCESS(state, payload) {
      state.refreshTokenLoading = false;
      state.refreshTokenSuccess = true;
      state.refreshTokenData = payload;
      state.accessToken = payload?.Token?.accessToken;
    },
    REFRESH_TOKEN_ERROR(state, payload) {
      state.refreshTokenLoading = false;
      state.refreshTokenSuccess = false;
      state.refreshTokenMsg = payload;
    },
    CLEAR_USER_DATA(state) {
      state.userMail = "";
      state.MCAdminCognitoID = "";
    },
    SAVE_ACCESS_TOKEN(state, payload) {
      state.accessToken = payload;
    },
    REMOVE_ACCESS_TOKEN(state) {
      state.accessToken = null;
    }
  },
  actions: {
    async LOGOUT({ commit }, payload?: { redirect?: boolean; force?: boolean }) {
      commit("RESET_LOGOUT_PROCESS");
      commit("LOADING");

      const shouldRedirect = payload?.redirect !== false;
      const forceLogout = payload?.force === true;

      try {
        const logoutData = await logout({
          AccessToken: TokenService.getAccessToken()
        });

        if (logoutData?.success || forceLogout) {
          commit("LOGOUT_SUCCESS", logoutData?.message ?? "Logged out");
          commit("REMOVE_ACCESS_TOKEN");
          commit("tenant/RESET_PROVISIONING", null, { root: true });

          await SignalRService.disconnect().catch(() => null);

          TokenService.clearStorage();
          if (shouldRedirect) {
            window.location.href = "/login";
          }
          return;
        }

        commit("LOGOUT_ERROR", logoutData?.message ?? "Logout failed");
      } catch (err) {
        if (forceLogout) {
          commit("LOGOUT_SUCCESS", "Logged out");
          commit("REMOVE_ACCESS_TOKEN");
          commit("tenant/RESET_PROVISIONING", null, { root: true });

          await SignalRService.disconnect().catch(() => null);

          TokenService.clearStorage();
          if (shouldRedirect) {
            window.location.href = "/login";
          }
          return;
        }

        const errorMessage = err instanceof Error ? err.message : "Network error during logout";
        commit("LOGOUT_ERROR", errorMessage);
      } finally {
        commit("LOADING_STOP");
      }
    },
    async REGISTER({ commit }, payload) {
      commit("RESET_REGISTER_PROCESS");
      commit("LOADING");
      commit("USER_MAIL", payload.EmailAddress);
      commit("RC_NUMBER", payload.RCNumber);

      let registerData = await register(payload);

      if (registerData?.success) {
        commit("REGISTER_SUCCESS", registerData);
        localStorage.setItem(
          "mcAdminUsername",
          registerData?.result?.data?.EmailAddress
        );
        commit("CLEAR_TEMP_REGISTER_INFO");
      } else if (registerData?.status === 400) {
        commit("REGISTER_ERROR", registerData?.result?.data?.errorMeassages[0]);
      } else {
        commit("REGISTER_ERROR", registerData?.result?.data?.Description);
      }
    },
    async LOGIN({ commit }, payload) {
      commit("RESET_LOGIN_PROCESS");
      commit("USER_MAIL", "");
      commit("MC_COGNITO_ID", "");
      commit("tenant/RESET_PROVISIONING", null, { root: true });
      await SignalRService.disconnect().catch(() => null);
      commit("LOADING");

      let loginData = await login(payload);

      if (loginData?.success) {
        commit("USER_MAIL", loginData?.result?.data?.EmailAddress);
        commit("MC_COGNITO_ID", loginData?.result?.data?.UserId);
        commit("LOGIN_SUCCESS", loginData?.result?.data);

        if (!loginData?.result?.data?.MfaEnabled) {
          commit("MFA_ENABLED", false);
          commit("SAVE_ACCESS_TOKEN", loginData?.result?.data?.Token?.accessToken);

          TokenService.setIdToken(loginData?.result?.data?.Token?.idToken);
          TokenService.setAccessToken(loginData?.result?.data?.Token?.accessToken);
          TokenService.setRefreshToken(loginData?.result?.data?.Token?.refreshToken);
          
          if (loginData?.result?.data?.SessionToken) {
            TokenService.setAccessToken(loginData?.result?.data?.SessionToken);
          }
        } else {
          commit("MFA_ENABLED", true);
          sessionStorage.setItem("sessionToken", loginData?.result?.data?.Session);
        }
      } else {
        commit("LOGIN_ERROR", loginData);
      }
    },
    async MFA_LOGIN({ commit }, payload) {
      commit("MFA_LOGIN_RESET");
      commit("LOADING");

      let mfaLoginData = await mfaLogin(payload);

      if (mfaLoginData?.success) {
        commit("MFA_LOGIN_SUCCESS", mfaLoginData?.result?.data?.Description);
        commit("SAVE_ACCESS_TOKEN", mfaLoginData?.result?.data?.Token?.accessToken);

        TokenService.setIdToken(mfaLoginData?.result?.data?.Token?.idToken);
        TokenService.setAccessToken(mfaLoginData?.result?.data?.Token?.accessToken);
        TokenService.setRefreshToken(mfaLoginData?.result?.data?.Token?.refreshToken);

        sessionStorage.removeItem("sessionToken");
      } else {
        commit("MFA_LOGIN_ERROR", mfaLoginData?.result?.data?.Description);
      }
    },
    async RESEND_OTP({ commit }, payload) {
      commit("RESET_OTP_PROCESS");
      commit("OTP_LOADING");
      let resendCodeData = await resendCode(payload);

      if (resendCodeData?.success) {
        commit("RESEND_OTP_SUCCESS", resendCodeData);
      } else {
        commit("RESEND_OTP_ERROR", resendCodeData);
      }

      commit("OTP_LOADING_STOP");
    },
    async CONFIRM_USER({ commit }, payload) {
      commit("RESET_OTP_PROCESS");
      commit("OTP_VALIDATION_LOADING");

      let verifyData = await confirmUser(payload);

      if (verifyData?.success) {
        commit("VALIDATION_SUCCESS", verifyData?.result?.data?.Description);
      } else {
        commit("VALIDATION_ERROR", verifyData?.result?.data?.Description);
      }
    },
    async FORGOT_PASSWORD({ commit }, payload) {
      commit("RESET_OTP_PROCESS");
      commit("OTP_LOADING");
      let forgotPasswordData = await forgotPassword(payload);

      if (forgotPasswordData?.success) {
        commit("OTP_SUCCESS", forgotPasswordData?.result?.data?.Description);
        commit("USER_MAIL", forgotPasswordData?.result?.data?.EmailAddress);
      } else {
        commit("OTP_ERROR", forgotPasswordData?.result?.data?.Description);
      }
    },
    async RESET_PASSWORD({ commit }, payload) {
      commit("RESET_PASSWORD_RESET");
      commit("LOADING");
      let resetPasswordData = await resetPassword(payload);

      if (resetPasswordData?.success) {
        commit("RESET_PASSWORD_SUCCESS",resetPasswordData?.result?.data?.Description);
      } else {
        commit("RESET_PASSWORD_ERROR", resetPasswordData?.result?.data?.Description);
      }
    },
    async GET_COMPANY_INFO({ commit }, payload) {
      commit("RESET_COMPANY_INFO");
      commit("LOADING_INFO");
      let getCompanyData = await getCompanyInfo(payload);

      if (getCompanyData?.success) {
        commit("GET_COMPANY_SUCCESS", getCompanyData);
      } else {
        commit("GET_COMPANY_ERROR", getCompanyData?.message);
      }

      commit("LOADING_INFO_STOP");
    },
    async REFRESH_TOKEN({ state, commit }) {
      try {
        commit("REFRESH_TOKEN_LOADING", true);

        const response = await refreshToken({ EmailAddress: state.userMail });

        if (response?.success) { 
          const newToken = response.result?.data?.Token?.accessToken;
  
          commit("REFRESH_TOKEN_SUCCESS", response.result?.data);
  
          TokenService.setAccessToken(newToken);
          return newToken;
        }

        commit("REFRESH_TOKEN_ERROR", response.message ?? "Refresh failed");
        throw new Error("Refresh failed");
      } catch (error) {
        throw error;
      } finally {
        commit("REFRESH_TOKEN_LOADING", false);
      }
    },
    async VERIFY_USER_LOGIN({ commit }, payload) {
      commit("RESET_VERIFY_USER");
      commit("VERIFY_USER_LOADING");

      let response = await verifyUserLogin(payload);

      if (response?.success) {
        commit("VERIFY_USER_SUCCESS", response?.message);
        commit("SAVE_ACCESS_TOKEN", response?.result?.data?.Token?.accessToken);

        TokenService.setIdToken(response?.result?.data?.Token?.idToken);
        TokenService.setAccessToken(response?.result?.data?.Token?.accessToken);
        TokenService.setRefreshToken(response?.result?.data?.Token?.refreshToken);

        sessionStorage.removeItem("sessionToken");
      } else {
        commit("VERIFY_USER_ERROR", response?.message);
      }
      commit("VERIFY_USER_LOADING_STOP");
    }
  },
};

export default auth;